import { BaseProduct, ProductBuilderMachineContext } from '../types';
import { productHasColour, productHasDayColour } from './colour';
import { productHasVariantWithMaterialType } from './material';

type GetCellDesigns = (context: ProductBuilderMachineContext) => string[];
type ProductHasCellDesignOption = (product: BaseProduct) => boolean;
type GetVariantOptionKeyForCellDesign = (
  product: BaseProduct,
) => CellDesignKey | null;

type CellDesignKey = 'option1' | 'option2' | 'option3';
type ProductHasVariantWithCellDesign = (
  cellDesign: string,
) => (product: BaseProduct) => boolean;

export const productHasCellDesignOption: ProductHasCellDesignOption = product =>
  product.options.includes('Cell Design');

export const getVariantOptionKeyForCellDesign: GetVariantOptionKeyForCellDesign = product => {
  const cellDesignIndex = product.options.findIndex(
    option => option === 'Cell Size' || option === 'Cell Design',
  );
  if (cellDesignIndex === -1) return null;

  return `option${cellDesignIndex + 1}` as CellDesignKey;
};

export const getCellDesigns: GetCellDesigns = context => {
  const {
    custom: { materialType, colour, colourDay, colourNight, dayColour },
  } = context;

  const colourFilter = (product: BaseProduct) =>
    typeof colour !== 'undefined' || typeof colourDay !== 'undefined'
      ? productHasColour(colour)(product) ||
        productHasColour(colourDay)(product)
      : true;

  const colourNightFilter = (product: BaseProduct) =>
    typeof colourNight !== 'undefined'
      ? productHasColour(colourNight)(product)
      : true;

  const daycolorFilter =
    typeof dayColour !== 'undefined'
      ? productHasDayColour(dayColour)
      : (product: BaseProduct) => true;

  const products = context.products
    .filter(productHasVariantWithMaterialType(materialType))
    .filter(colourFilter)
    .filter(colourNightFilter)
    .filter(daycolorFilter);

  const cellDesignKey =
    products.length > 0 ? getVariantOptionKeyForCellDesign(products[0]) : null;

  if (cellDesignKey === null) return [];

  return Array.from(
    new Set(
      products
        .map(({ variants }) =>
          variants.map(variant => {
            return variant[cellDesignKey] as string;
          }),
        )
        .flat(),
    ),
  );
};

export const productHasVariantWithCellDesign: ProductHasVariantWithCellDesign = cellDesign => product => {
  const optionKey = getVariantOptionKeyForCellDesign(product);
  if (optionKey === null) return false;

  return product.variants.some(variant => variant[optionKey] === cellDesign);
};
